/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Focused Practice Practice Page - Read-only review of completed practice questions
 *
 * This page is ONLY used for reviewing completed practice answers in read-only mode.
 * All actual question answering happens in ChallengeSession.jsx.
 * Navigation to this page should include ?mode=review query parameter.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import ChallengeQuestionRenderer from '../components/ChallengeQuestionRenderer'
import { Target, ChevronLeft, ChevronRight, CheckCircle2, Eye, ArrowLeft } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import api from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('FocusedPracticePractice')

const FocusedPracticePractice = () => {
  const { sessionId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentSession, loading, getSession } = useFocusedPracticeStore()

  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  // Check if we're in review mode
  const isReviewMode = searchParams.get('mode') === 'review'

  useEffect(() => {
    loadPractice()
  }, [sessionId, isReviewMode])

  const loadPractice = async () => {
    // If not in review mode, redirect to the review page
    if (!isReviewMode) {
      navigate(`/focused-practice/${sessionId}/review`, { replace: true })
      return
    }

    setIsLoading(true)

    // Get session to validate and get challenge ID
    const sessionResult = await getSession(sessionId)
    if (!sessionResult.success) {
      toast.error(getErrorMessage(sessionResult.error))
      navigate('/focused-practice')
      return
    }

    const challengeId = sessionResult.data.challengeId
    if (!challengeId) {
      toast.error('No practice challenge found')
      navigate(`/focused-practice/${sessionId}/review`)
      return
    }

    // Load questions and answers for review
    await loadQuestionsForReview(challengeId)
    setIsLoading(false)
  }

  const loadQuestionsForReview = async (challengeId) => {
    try {
      // Get the challenge to find stage info
      const challengeResult = await api.get(`/challenges/${challengeId}`)
      if (!challengeResult.data?.success || !challengeResult.data?.data?.stages?.length) {
        toast.error('Failed to load practice data')
        navigate(`/focused-practice/${sessionId}/review`)
        return
      }

      const stage = challengeResult.data.data.stages[0]

      // Map questions
      const mappedQuestions = (stage.questions || [])
        .filter((sq) => sq.question !== null && sq.question !== undefined)
        .map((sq) => {
          const questionId = sq.question?.question_id || sq.question?.questionId || sq.question?.id
          return {
            ...sq.question,
            question_id: questionId,
            questionId: questionId,
            stageQuestionId: sq.id,
            questionOrder: sq.questionOrder
          }
        })

      if (mappedQuestions.length === 0) {
        toast.error('No questions found')
        navigate(`/focused-practice/${sessionId}/review`)
        return
      }

      setQuestions(mappedQuestions)

      // Load answers
      const responsesResult = await api.get(
        `/challenges/${challengeId}/stages/${stage.id}/responses`
      )
      if (responsesResult.data?.success && responsesResult.data?.data) {
        const existingAnswers = {}
        responsesResult.data.data.forEach((r) => {
          existingAnswers[r.questionId] = r.userAnswer
        })
        setAnswers(existingAnswers)
      }
    } catch (err) {
      logger.error('Failed to load questions for review:', err)
      toast.error('Failed to load practice data')
      navigate(`/focused-practice/${sessionId}/review`)
    }
  }

  const currentQuestion = questions[currentQuestionIndex]
  const totalQuestions = questions.length
  const answeredCount = Object.keys(answers).filter(
    (id) => answers[id] !== null && answers[id] !== ''
  ).length

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1)
    }
  }

  const jumpToQuestion = (index) => {
    setCurrentQuestionIndex(index)
  }

  const handleBackToReview = () => {
    navigate(`/focused-practice/${sessionId}/review`)
  }

  // Loading state
  if (loading || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  // No questions loaded
  if (questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <p className="text-yellow-800">No questions available for review.</p>
          <button
            onClick={handleBackToReview}
            className="mt-4 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
          >
            Back to Review
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackToReview}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="p-2 bg-primary-100 rounded-lg">
            <Target className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              {currentSession?.name || 'Focused Practice'}
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                Review Mode
              </span>
            </h1>
            <p className="text-sm text-gray-600">{currentSession?.targetDomain}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-6">
          {/* Completed badge */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 rounded-lg">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Completed</span>
          </div>

          {/* Progress count */}
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">
              {currentQuestionIndex + 1} / {totalQuestions}
            </p>
            <p className="text-sm text-gray-600">{answeredCount} answered</p>
          </div>
        </div>
      </div>

      {/* Question Navigation Pills */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {questions.map((q, idx) => {
            const qId = q.questionId || q.question_id
            const isAnswered = answers[qId] !== undefined && answers[qId] !== ''
            const isCurrent = idx === currentQuestionIndex

            return (
              <button
                key={qId}
                onClick={() => jumpToQuestion(idx)}
                className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                  isCurrent
                    ? 'bg-primary-600 text-white ring-2 ring-primary-300 ring-offset-1'
                    : isAnswered
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                {idx + 1}
              </button>
            )
          })}
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        {currentQuestion ? (
          <ChallengeQuestionRenderer
            question={currentQuestion}
            userAnswer={answers[currentQuestion.questionId || currentQuestion.question_id] || ''}
            readOnly={true}
            showCorrectAnswer={true}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading question...</p>
          </div>
        )}
      </div>

      {/* Review mode info */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-6">
        <div className="flex items-start gap-2">
          <Eye className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-primary-800">
            You are reviewing your answers. Correct answers and rationales are shown for each
            question.
          </p>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          Previous
        </button>

        <button
          onClick={handleBackToReview}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Review
        </button>

        <button
          onClick={handleNext}
          disabled={currentQuestionIndex === totalQuestions - 1}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

export default FocusedPracticePractice
